import React from 'react';

const AdminNav = ({
  sidebarOpen,
  setSidebarOpen,
  colors,
  currentTime,
  toggleDarkMode,
  handleLogout,
  darkMode,
  isMobile,
  isTablet,
  user // Add user prop for property info
}) => {
  const formatDate = () => {
    return currentTime.toLocaleDateString('en-US', {
      weekday: isMobile ? 'short' : 'long',
      year: 'numeric',
      month: isMobile ? 'short' : 'long',
      day: 'numeric'
    });
  };

  const formatTime = () => {
    return currentTime.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: isMobile ? undefined : '2-digit',
      hour12: true
    });
  };

  const styles = {
    topBar: {
      position: 'sticky',
      top: 0,
      padding: isMobile ? '12px 16px' : '14px 28px',
      background: colors.white,
      borderBottom: `1px solid ${colors.mediumGray}`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      zIndex: 100,
      gap: '16px',
      minHeight: isMobile ? '60px' : '68px'
    },
    leftSection: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px'
    },
    hamburgerButton: {
      display: isMobile ? 'flex' : 'none',
      width: '42px',
      height: '42px',
      borderRadius: '10px',
      border: `2px solid ${colors.primary}`,
      background: colors.white,
      cursor: 'pointer',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '18px',
      color: colors.primary,
      transition: 'all 0.2s ease',
      flexShrink: 0
    },
    toggleButton: {
      position: 'fixed',
      top: '16px',
      left: sidebarOpen ? '300px' : '86px',
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      border: `2px solid ${colors.mediumGray}`,
      background: colors.white,
      cursor: 'pointer',
      display: isMobile ? 'none' : 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '12px',
      fontWeight: '700',
      color: colors.primary,
      transition: 'left 0.3s ease, all 0.2s ease',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      zIndex: 1001
    },
    // FIXED: Clock container with proper alignment
    clockContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: isMobile ? '8px 14px' : '10px 18px',
      borderRadius: '12px',
      background: colors.lightGray,
      border: `1px solid ${colors.mediumGray}`
    },
    clockIconWrapper: {
      width: isMobile ? '38px' : '44px',
      height: isMobile ? '38px' : '44px',
      borderRadius: '10px',
      background: `linear-gradient(135deg, ${colors.primary}, ${colors.accent})`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    },
    clockIcon: {
      fontSize: isMobile ? '18px' : '22px',
      lineHeight: 1
    },
    clockInfo: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      gap: '2px'
    },
    dateText: {
      fontSize: isMobile ? '13px' : '15px',
      fontWeight: '700',
      color: colors.textDark,
      lineHeight: 1.2,
      whiteSpace: 'nowrap'
    },
    timeText: {
      fontSize: isMobile ? '12px' : '13px',
      color: colors.textLight,
      fontWeight: '600',
      fontFamily: '"SF Mono", "Roboto Mono", monospace',
      letterSpacing: '0.5px',
      lineHeight: 1.2
    },
    propertyBadge: {
      display: isMobile ? 'none' : 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '8px 16px',
      borderRadius: '12px',
      background: `linear-gradient(135deg, ${colors.primary}, ${colors.accent})`,
      border: `2px solid ${colors.primary}`,
      boxShadow: '0 2px 8px rgba(45, 80, 22, 0.15)'
    },
    propertyIcon: {
      fontSize: '20px',
      lineHeight: 1
    },
    propertyInfo: {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px'
    },
    propertyName: {
      fontSize: '14px',
      fontWeight: '700',
      color: colors.white,
      lineHeight: 1.2
    },
    propertyCode: {
      fontSize: '11px',
      color: 'rgba(255, 255, 255, 0.8)',
      fontWeight: '600',
      letterSpacing: '0.5px',
      lineHeight: 1.2
    },
    rightSection: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    logoutButton: {
      padding: isMobile ? '10px 16px' : '10px 20px',
      borderRadius: '10px',
      border: 'none',
      background: `linear-gradient(135deg, ${colors.danger}, #dc2626)`,
      color: colors.white,
      cursor: 'pointer',
      fontSize: isMobile ? '13px' : '14px',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(239, 68, 68, 0.25)'
    }
  };

  return (
    <>
      {/* Toggle Button - Desktop Only */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        style={styles.toggleButton}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = colors.primary;
          e.currentTarget.style.color = colors.white;
          e.currentTarget.style.borderColor = colors.primary;
          e.currentTarget.style.transform = 'scale(1.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = colors.white;
          e.currentTarget.style.color = colors.primary;
          e.currentTarget.style.borderColor = colors.mediumGray;
          e.currentTarget.style.transform = 'scale(1)';
        }}
      >
        {sidebarOpen ? '«' : '»'}
      </button>

      {/* Top Bar */}
      <div style={styles.topBar}>
        {/* Left Section */}
        <div style={styles.leftSection}>
          {/* Hamburger Menu - Mobile Only */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            style={styles.hamburgerButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = colors.primary;
              e.currentTarget.style.color = colors.white;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = colors.white;
              e.currentTarget.style.color = colors.primary;
            }}
          >
            ☰
          </button>

          {/* Clock Display - FIXED ALIGNMENT */}
          <div style={styles.clockContainer}>
            <div style={styles.clockIconWrapper}>
              <span style={styles.clockIcon}>🕐</span>
            </div>
            <div style={styles.clockInfo}>
              <div style={styles.dateText}>
                {formatDate()}
              </div>
              <div style={styles.timeText}>
                {formatTime()}
              </div>
            </div>
          </div>
        </div>

        {/* Property Badge - Desktop Only */}
        {user?.propertyName && (
          <div style={styles.propertyBadge}>
            <span style={styles.propertyIcon}>🏨</span>
            <div style={styles.propertyInfo}>
              <div style={styles.propertyName}>{user.propertyName}</div>
              <div style={styles.propertyCode}>{user.propertyCode || 'N/A'}</div>
            </div>
          </div>
        )}

        {/* Right Section - REMOVED DARK THEME BUTTON */}
        <div style={styles.rightSection}>
          <button
            onClick={handleLogout}
            style={styles.logoutButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.35)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(239, 68, 68, 0.25)';
            }}
          >
            🚪 {!isMobile && 'Logout'}
          </button>
        </div>
      </div>
    </>
  );
};

export default AdminNav;